import { useMemo } from 'react';
import { WS2_AMENITY_NAMES } from '../../../../constants/amenities';
import { CONFIDENCE_DOT_COLOR } from '../../../../constants/confidenceBadge';
import { getDisplay, getInstructions } from '../../listingDisplay';
import { formatAvailabilitySummary } from '../../../../utils/availabilitySchedule';

function truncate(text, max = 120) {
    if (!text) return '';
    if (text.length <= max) return text;
    return `${text.slice(0, max)}…`;
}

function resolveAmenitySlugs(listing) {
    const confirmed = listing?.confirmedFields?.amenities;
    if (Array.isArray(confirmed) && confirmed.length > 0) return confirmed;
    const enriched = listing?.enrichmentFields?.amenities;
    if (Array.isArray(enriched)) {
        return enriched.map((a) => a?.slug || a?.name || a).filter(Boolean);
    }
    return [];
}

export default function PropertyDetailsPreview({ listing }) {
    const enrichmentAmenities = listing?.enrichmentFields?.amenities ?? [];

    const amenityChips = useMemo(() => {
        if (!listing) return [];
        const confidenceBySlug = Object.fromEntries(
            enrichmentAmenities.map((a) => [a.slug, a.confidence ?? 'medium']),
        );
        return resolveAmenitySlugs(listing).map((slug) => ({
            slug,
            label: WS2_AMENITY_NAMES[slug] ?? slug,
            confidence: confidenceBySlug[slug] ?? 'medium',
        }));
    }, [listing, enrichmentAmenities]);

    const availabilitySummary = useMemo(
        () => formatAvailabilitySummary(listing?.confirmedFields?.availability),
        [listing?.confirmedFields?.availability],
    );

    if (!listing) return null;

    const { name, type } = getDisplay(listing);
    const instructions = getInstructions(listing);

    const hasContent = name || type || instructions
        || availabilitySummary.length > 0
        || amenityChips.length > 0;

    return (
        <div className="host-panel-section property-details-preview">
            <div className="host-panel-section-label">Property details</div>
            {hasContent ? (
                <div className="property-details-preview-card">
                    {name && (
                        <p className="property-details-preview-name">{name}</p>
                    )}
                    {type && (
                        <p className="property-details-preview-type">{type}</p>
                    )}
                    {instructions && (
                        <p className="property-details-preview-instructions">
                            &ldquo;{truncate(instructions)}&rdquo;
                        </p>
                    )}
                    {availabilitySummary.length > 0 && (
                        <div className="property-details-preview-availability">
                            <p className="property-details-preview-availability-label">
                                Availability
                            </p>
                            {availabilitySummary.map((line, i) => (
                                <p key={i} className="property-details-preview-availability-line">
                                    {line}
                                </p>
                            ))}
                        </div>
                    )}
                    {amenityChips.length > 0 && (
                        <div className="property-details-preview-amenities">
                            {amenityChips.map(({ slug, label, confidence }) => (
                                <span
                                    key={slug}
                                    className="property-details-preview-amenity-chip"
                                >
                                    <span
                                        className="property-details-preview-amenity-dot"
                                        style={{
                                            backgroundColor:
                                                CONFIDENCE_DOT_COLOR[confidence]
                                                ?? CONFIDENCE_DOT_COLOR.medium,
                                        }}
                                        aria-hidden
                                    />
                                    {label}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <p className="property-details-preview-empty">Property details not confirmed.</p>
            )}
        </div>
    );
}
